const MOBILE_MAX_HEIGHT = 480;
const DESKTOP_MAX_HEIGHT = 720;

/**
 * Turn an HLS variant playlist URI into the matching progressive MP4 URL.
 * Resolving against the master URL keeps absolute and relative variant URIs valid.
 * e.g. `abc.HD-720p.4.5Mbps.hls.95117695_.m3u8` → `abc.HD-720p-4.5Mbps-95117695.mp4`
 * @param {string} masterUrl
 * @param {string} variantUri
 * @returns {string}
 */
function mp4FromHlsVariant(masterUrl, variantUri) {
  const url = new URL(variantUri, new URL(masterUrl, window.location.href));

  url.search = '';
  url.pathname = url.pathname
    .replace(/\.m3u8$/, '')
    .replace(/\.hls\.(\d+)_$/, '-$1')
    .replace(/\.(HD|SD)-(\d+p)\./, '.$1-$2-')
    .concat('.mp4');

  return url.href;
}

/**
 * Prefer the largest MP4 at or under the height cap (480p on phones, 720p
 * otherwise). Liquid only exposes the 1080p file, so the HLS playlist is the
 * source of the smaller renditions. Falls back to data-src.
 * @param {HTMLVideoElement} video
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
async function resolveSrc(video, signal) {
  const fallback = video.dataset.src || '';
  const playlist = video.dataset.hls;

  if (!playlist) return fallback;

  const maxHeight = window.matchMedia('(max-width: 749px)').matches ? MOBILE_MAX_HEIGHT : DESKTOP_MAX_HEIGHT;

  try {
    const response = await fetch(playlist, { signal });

    if (!response.ok) return fallback;

    const lines = (await response.text()).split('\n');
    let variant = '';
    let bestHeight = 0;

    for (let i = 0; i < lines.length; i++) {
      const match = lines[i].match(/RESOLUTION=\d+x(\d+)/);
      const uri = lines[i + 1]?.trim();

      if (!match || !uri || uri.startsWith('#')) continue;

      const height = Number(match[1]);

      if (height <= maxHeight && height > bestHeight) {
        bestHeight = height;
        variant = uri;
      }
    }

    return variant ? mp4FromHlsVariant(playlist, variant) : fallback;
  } catch (error) {
    if (error.name === 'AbortError') return '';

    return fallback;
  }
}

if (!customElements.get('lazy-video')) {
  class LazyVideo extends HTMLElement {
    /** @type {HTMLVideoElement | null} */
    video = null;

    /** @type {HTMLButtonElement | null} */
    toggle = null;

    /** @type {IntersectionObserver | null} */
    observer = null;

    /** @type {AbortController | undefined} */
    abortController = undefined;

    /** @type {HTMLImageElement | null} */
    posterImage = null;

    intersecting = false;
    resolving = false;
    userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    constructor() {
      super();
      this.onIntersect = this.onIntersect.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.updateToggle = this.updateToggle.bind(this);
      this.onPlaying = this.onPlaying.bind(this);
    }

    connectedCallback() {
      this.video = this.querySelector('video');

      if (!this.video) return;

      this.toggle = this.querySelector('[data-lazy-video-toggle]');
      this.toggle?.addEventListener('click', this.onToggle);
      this.video.addEventListener('play', this.updateToggle);
      this.video.addEventListener('pause', this.updateToggle);
      this.video.addEventListener('playing', this.onPlaying);
      this.updateToggle();

      this.observer = new IntersectionObserver(this.onIntersect, { rootMargin: '200px 0px' });
      this.observer.observe(this.video);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
      this.abortController?.abort();
      this.toggle?.removeEventListener('click', this.onToggle);
      this.video?.removeEventListener('play', this.updateToggle);
      this.video?.removeEventListener('pause', this.updateToggle);
      this.video?.removeEventListener('playing', this.onPlaying);
      this.video?.pause();
      this.intersecting = false;
      this.resolving = false;
    }

    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    onIntersect(entries) {
      for (const entry of entries) {
        this.intersecting = entry.isIntersecting;

        if (entry.isIntersecting) {
          this.showPoster();
          this.play();
        } else {
          this.video?.pause();
        }
      }
    }

    /**
     * Lay the poster over the video as its own image, added only near the viewport so it does
     * not compete with the LCP image. Not the video's `poster` attribute: Safari drops that the
     * moment `src` is set and shows nothing until the first frame decodes, which read as a blink.
     */
    showPoster() {
      const posterUrl = this.video?.dataset.poster;

      if (!posterUrl || this.posterImage) return;

      const img = document.createElement('img');
      img.className = 'lazy-video__poster';
      img.src = posterUrl;
      img.alt = '';
      img.decoding = 'async';
      img.setAttribute('aria-hidden', 'true');
      this.video.insertAdjacentElement('afterend', img);
      this.posterImage = img;
    }

    /** Fade the poster out only once the video has painted a frame, so nothing blank shows between them. */
    onPlaying() {
      const video = this.video;

      if (!video || !this.posterImage) return;

      const hide = () => this.posterImage?.classList.add('lazy-video__poster--hidden');

      if ('requestVideoFrameCallback' in video) {
        video.requestVideoFrameCallback(hide);
      } else {
        video.addEventListener('timeupdate', hide, { once: true });
      }
    }

    /**
     * @param {MouseEvent} event
     */
    onToggle(event) {
      event.preventDefault();

      if (!this.video) return;

      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    }

    updateToggle() {
      if (!this.toggle || !this.video) return;

      const isPaused = this.video.paused;
      const playIcon = this.toggle.querySelector('[data-icon-play]');
      const pauseIcon = this.toggle.querySelector('[data-icon-pause]');

      this.toggle.setAttribute('aria-pressed', String(!isPaused));

      // SVG elements have no `hidden` property, so set the attribute directly
      playIcon?.toggleAttribute('hidden', !isPaused);
      pauseIcon?.toggleAttribute('hidden', isPaused);
    }

    async play() {
      const video = this.video;

      if (!video || !this.intersecting || this.userPaused) return;

      if (video.getAttribute('src')) {
        video.play().catch(() => {});
        return;
      }

      if (this.resolving) return;

      this.resolving = true;
      this.abortController?.abort();
      this.abortController = new AbortController();

      const src = await resolveSrc(video, this.abortController.signal);

      this.resolving = false;

      if (!src || !video.isConnected || video.getAttribute('src')) return;

      video.src = src;

      if (!this.intersecting || this.userPaused) return;

      video.play().catch(() => {});
    }
  }

  customElements.define('lazy-video', LazyVideo);
}
